import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { CircularProgress } from '@material-ui/core';
import SortableTree from 'react-sortable-tree';
import 'react-sortable-tree/style.css';
import {
  Close,
  Edit,
  Delete,
  Check,
  CheckCircleOutline,
} from '@material-ui/icons';
import { CancelTokenSource } from 'axios';
import { RootState } from '~/store/config';
import { ToastMessage, useTranslation } from '~/dependencies';
import * as NuacomComponent from '~/components';
import messages from '~/validations/messagesNotifications';
import { colors } from '~/layout';
import * as actions from '~/store/actions';
import api from '~/services';
import { electron, log } from '~/helpers';
import {
  Container,
  TagsCardHeader,
  InputField,
  ListTags,
  ButtonNewTag,
  BoxNewTag,
  Item,
  InputTagName,
  BoxValidation,
  ActionsContainer,
} from './styles';

import './styles.css';

const TAG_DEFAULT_COLORS = colors.colorTags;
const TAG_LEVEL_SHRINK_PX = 40; // Each nesting level shrinks the row by this much.
const TAG_NAME_MAX_LEGNTH = 20;
const LOG_PREFIX = 'NUACOM-LOG: [TAGS] -';

type Tag = {
  id: number;
  parentId?: number;
  accountId: number;
  assigned: number;
  tagName: string;
  availableFor: string;
  orderColumn: number;
  tagColor: string;
  title: string;
  level: number;
  expanded: boolean;
  isEditing: boolean;
  children: Tag[];
};

type TagsTree = Array<Tag>;

type TagMap = {
  [key: number]: Tag;
};

/** This function recursively traverses through the tags and their children, adding the level property to each tag. */
function addTagLevels(tags: TagsTree, level: number = 0) {
  tags.forEach(tag => {
    tag.level = level;
    if (tag.children && tag.children.length > 0) {
      addTagLevels(tag.children, level + 1);
    }
  });
  return tags;
}

function groupTagsChildren(tags: TagsTree) {
  const tagsClone = JSON.parse(JSON.stringify(tags));

  const tagMapRef: TagMap = {};
  const rootTags: TagsTree = [];

  tagsClone.forEach((tag: Tag) => {
    tagMapRef[tag.id] = tag;
  });

  const hasParent = (tag: Tag) => !!tag.parentId;

  tagsClone.forEach((tag: Tag) => {
    if (hasParent(tag)) {
      const parentTag = tagMapRef[tag.parentId!];
      if (parentTag) {
        parentTag.children.push(tag);
      }
    } else {
      rootTags.push(tag);
    }
  });

  return addTagLevels(rootTags);
}

function filterTagsTreeByTagName(tagsTree: TagsTree, tagName: string) {
  if (tagsTree?.length && tagName) {
    const parentTags = tagsTree.filter(tag => tag.parentId === null);
    const filteredTags = parentTags.filter(e =>
      e.tagName.toUpperCase().includes(tagName.toUpperCase())
    );

    return filteredTags;
  }

  return tagsTree;
}

function getSortableTreeCustomStyles(tag: Tag) {
  const shrink = (tag.level || 0) * TAG_LEVEL_SHRINK_PX;
  return {
    fontSize: 10,
    color: 'white',
    borderRadius: 5,
    cursor: 'pointer',
    width: shrink ? `calc(100% - ${shrink}px)` : '100%',
    maxWidth: '100%',
    backgroundColor: tag?.tagColor ? tag.tagColor : 'gray',
  };
}

function visibilityCamelCaseParser(visibilities: any) {
  return visibilities.map((visibility: any) => ({
    type: visibility.type,
    allowedId: visibility.allowed_id,
  }));
}

function orderBasedOnBaseArray(baseArray: Tag[], arrayToOrder: Tag[]) {
  const orderMap = new Map();
  baseArray.forEach((item, index) => {
    orderMap.set(item.id, index);
  });

  return arrayToOrder.sort((a, b) => {
    const orderA = orderMap.get(a.id);
    const orderB = orderMap.get(b.id);
    return orderA - orderB;
  });
}

type Props = {
  open: boolean;
  callId: string;
  sipCallId?: string;
  originalSipCallId?: string;
  onClose(value: boolean): void;
  callInfo: { directionNumber: number };
};

export default function AddTags({
  open,
  callId,
  sipCallId,
  originalSipCallId,
  onClose,
  callInfo,
}: Props) {
  // REF's
  const apiRequests = useRef<CancelTokenSource[]>([]);

  // STATES
  const [search, setSearch] = useState('');
  const [allTags, setAllTags] = useState<TagsTree>([]);
  const [allTagsDetails, setAllTagsDetails] = useState<Tag[]>([]);
  const [showAddNewTag, setShowAddNewTag] = useState(false);
  const [nameNewTag, setNameNewTag] = useState('');
  const [colorNewTag, setColorNewTag] = useState<string | null>(null);
  const [deleteTagId, setDeleteTagId] = useState<number | null>(null);
  const [updatedTagName, setUpdatedTagName] = useState<string | null>('');
  const [openConfirmDeletenBox, setOpenConfirmDeletenBox] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // CONST's
  const tagsTree = allTags && allTags.length ? groupTagsChildren(allTags) : [];

  // HOOKS
  const dispatch = useDispatch();
  const { t } = useTranslation();

  // SELECTORS
  const isAdministrator = useSelector(
    (state: RootState) => state.auth?.isAdmin
  );
  const userExtension = useSelector(
    (state: RootState) => state.auth.userExtension
  );
  // Monitor when show tags component has change, e.g tags unsigned
  const tagsReducer = useSelector((state: RootState) => state.tags.data);

  // MEMO's
  const showTagsHasChanged = useMemo(() => tagsReducer, [tagsReducer]);

  // const getCancelToken = () => {
  //   const cancelToken = axios.CancelToken.source();
  //   apiRequests.current = [...apiRequests.current, cancelToken];
  //   return cancelToken.token;
  // };

  /**
   * Fetch call tags, standardize in a format which `SortableTree` undestands
   * Group tags by parent_id and also add a tag level which indicate the tree position/location
   */
  const getAlltags = useCallback(async () => {
    try {
      const { directionNumber: availableFor } = callInfo;

      // Fetch all tags and also tags details
      const allCallTags = (
        await api.tags.fetchCallTags(
          callId,
          sipCallId || callId,
          originalSipCallId,
          {
            // cancelToken: getCancelToken(),
          }
        )
      )?.data;

      const allTagsDetailsResponse = (
        await api.tags.fetchTagsDetails(availableFor, {
          // cancelToken: getCancelToken(),
        })
      )?.data?.data;

      // Marge additional tag details such as visibility in order to create a single allTags object
      let allTagsWithMergedDetails = allCallTags.map((callTagInfo: any) => {
        const additionalDetails = allTagsDetailsResponse.find(
          (tagDetails: any) => tagDetails.id === callTagInfo.id
        );

        if (additionalDetails) {
          return {
            visibility: [],
            ...additionalDetails,
            ...callTagInfo,
          };
        }

        if (callTagInfo.parent_id === null) return undefined;

        return callTagInfo;
      });

      allTagsWithMergedDetails = allTagsWithMergedDetails.filter(
        (tag: any) => tag
      );

      let allTagsAllowedUserExtension;

      if (isAdministrator) {
        // Admin can view all tags
        allTagsAllowedUserExtension = allTagsWithMergedDetails;
      } else {
        // Filter for user allowed extension tags
        allTagsAllowedUserExtension = allTagsWithMergedDetails.filter(
          (callTagInfo: any) => {
            if (callTagInfo.parent_id) return true;

            if (callTagInfo?.visibility === undefined) return false;

            if (
              Array.isArray(callTagInfo.visibility) &&
              callTagInfo.visibility.length === 0
            )
              return true;

            if (
              callTagInfo.visibility.find(
                (visibilityInfo: any) =>
                  visibilityInfo.allowed_id === Number(userExtension)
              )
            )
              return true;

            return false;
          }
        );
      }

      // Format tags object in list tags understandable structure
      const allTagsStandard = allTagsAllowedUserExtension?.map((tag: any) => ({
        id: tag.id,
        parentId: tag?.parent_id,
        accountId: tag.account_id,
        assigned: tag.assigned,
        tagName: tag.tag_name,
        availableFor: tag.available_for,
        orderColumn: tag.order_column,
        tagColor: tag.tag_color,
        title: tag.tag_name,
        visibility: tag?.visibility
          ? visibilityCamelCaseParser(tag.visibility)
          : [],
        descendantsCount: tag?.descendants_count
          ? tag?.descendants_count
          : null,
        expanded: tag.assigned === 1,
        isEditing: false,
        children: [],
      }));

      setAllTagsDetails(allTagsDetailsResponse);
      setAllTags(allTagsStandard);

      return allTagsStandard;
    } catch (err) {
      log.error('Failed to execute the getAlltags function.', err);
    }

    return [];
  }, [
    callId,
    sipCallId,
    originalSipCallId,
    callInfo,
    userExtension,
    isAdministrator,
  ]);

  const scrollCallDetailsContainer = useCallback(
    async (distanceTopPixels: number, distanceLeftPixels: number) => {
      try {
        setTimeout(() => {
          const detailsContainer = document.querySelector(
            '[aria-labelledby="call-details-modal-container"]'
          );
          if (detailsContainer) {
            detailsContainer?.scrollBy({
              top: distanceTopPixels,
              left: distanceLeftPixels,
              behavior: 'smooth',
            });
          }
        }, 100);
      } catch (err) {
        log.error(
          'Failed to execute the scrollCallDetailsContainer function.',
          err
        );
      }
    },
    []
  );

  useEffect(() => {
    if (Object.keys(showTagsHasChanged).includes(callId)) {
      getAlltags();
    }

    return () => {
      apiRequests.current.map(e => e.cancel(`Cancelling request...`));
      apiRequests.current = [];
    };
  }, [getAlltags, showTagsHasChanged, callId]);

  useEffect(() => {
    if (open) scrollCallDetailsContainer(200, 0);
  }, [open, scrollCallDetailsContainer]);

  /**
   * Edition Step 1: Set tag as editing state
   */
  function initiateTagEdit(editTagId: number, tagName: string) {
    const allTagsAsNoEditingState = allTags.map((tag: Tag) => ({
      ...tag,
      isEditing: false,
    }));

    allTagsAsNoEditingState.forEach(tag => {
      if (tag.id === editTagId) {
        tag.isEditing = true;
      }
    });

    setUpdatedTagName(tagName);
    setAllTags(allTagsAsNoEditingState);
  }

  /**
   * Edition Step 2: Update the tag via API, request all tags again and notify the user
   */
  async function confirmEdition(tagId: number) {
    try {
      await api.tags.patch.updateTag({
        tag_id: Number(tagId),
        tag_name: updatedTagName,
      });

      getAlltags();

      ToastMessage('success', messages(updatedTagName).CALLS.tags.edit.success);

      clearEditionState();
    } catch (err) {
      log.error('Failed to execute the confirmEdition function.', err);
    }
  }

  async function assignTag(tagId: number, tagName: string) {
    try {
      await dispatch(actions.addCallTagById(callId, tagId) as any);

      ToastMessage('info', messages(tagName).CALLS.tags.assign.success);

      electron.log(
        `${LOG_PREFIX} TagId: ${tagId} (${tagName}) has been assigned to callId: ${callId}`
      );
    } catch (err) {
      ToastMessage('error', messages(tagName).CALLS.tags.assign.error);

      electron.log(
        `${LOG_PREFIX} Failed to assign tagId: ${tagId} to callId: ${callId}.
      Reason: ${err instanceof Error ? err?.message : 'Unknown error'}`,
        err
      );
    }
  }

  async function createTag() {
    try {
      if (!nameNewTag?.length) return;

      if (!colorNewTag) {
        setError(t('components.addTags.selectColorError'));
        return;
      }

      setLoading(true);

      // Create the new tag
      const newestTag = (
        await api.tags.post.createTag({
          tag_name: nameNewTag,
          tag_color: colorNewTag,
        })
      ).data;

      if (newestTag?.id) {
        ToastMessage(
          'success',
          messages(updatedTagName).CALLS.tags.new.success
        );

        electron.log(
          `${LOG_PREFIX} TagId: ${newestTag.id} (${nameNewTag}) has been created successfully.`
        );
      }

      // Bring the current tags for a specific call id.
      const curCallTags =
        // await api.tags.get.getTag(callId.split('.').join('*'))
        (await api.tags.get.getTag(callId)).data;

      // Update the local tag list by call id.
      dispatch(
        actions.fetchTagsSuccess({
          callId,
          tags: curCallTags,
        })
      );

      const allTagsUpdated = await getAlltags();

      // Find the new tag id.
      const newestTagInfo = allTagsUpdated.filter(
        (e: any) => e.id === newestTag?.id
      );

      // Associate the new tag with this call id.
      if (newestTagInfo.length) {
        assignTag(newestTagInfo[0].id, newestTagInfo[0].tagName);
      }

      setError(null);
    } catch (err) {
      electron.log(`${LOG_PREFIX} Failed to create tag ${nameNewTag}.`, err);
    } finally {
      setLoading(false);
    }
  }

  /**
   * Deletion Step 3: Delete the tagId, call the api to get updated tags and notificate the users about the deletion completed.
   */
  async function deleteTag(tagId: number) {
    try {
      if (!tagId) return;

      await api.tags.delete.tag({ tag_id: tagId });

      // The backend takes some time to delete the tag and update the calls tags, so we need to wait a bit before request the updated tags list.
      setTimeout(() => {
        getAlltags();
      }, 500);

      ToastMessage(
        'success',
        messages(updatedTagName).CALLS.tags.delete.success
      );

      electron.log(
        `${LOG_PREFIX} TagId: ${tagId} has been deleted successfully.`
      );
    } catch (err) {
      electron.log(`${LOG_PREFIX} Failed to delete tagId: ${tagId}.`, err);
    }
  }

  async function expandAndAssignTag(tagInfo: Tag) {
    const { id: tagId, tagName, assigned, isEditing } = tagInfo;

    if (!assigned && !isEditing) {
      await assignTag(tagId, tagName);
    }

    const expandedTags = allTags.map((tag: any) => ({
      ...tag,
      ...(tag.id === tagId && { expanded: !tag.expanded }),
    }));

    setAllTags(expandedTags);
  }

  /**
   * Cancel tag name edition and set isEdition to false in order to hide the edit input
   */
  function clearEditionState() {
    // FIXME: Duplicate above
    const allTagsAsNoEditingState = allTags.map(tag => ({
      ...tag,
      isEditing: false,
    }));

    setAllTags(allTagsAsNoEditingState);

    setUpdatedTagName(null);
  }

  /**
   * Handle on close add tags card by clickig on "X" right hand side
   */
  function handleClose() {
    onClose(false);
    setShowAddNewTag(false);
    setNameNewTag('');
    setColorNewTag(null);
  }

  function cancel() {
    setShowAddNewTag(false);
    setNameNewTag('');
    setColorNewTag(null);
  }

  function addTagName(text: string) {
    if (text.length <= TAG_NAME_MAX_LEGNTH) {
      setNameNewTag(text);
    }
  }

  function addColor(color: string) {
    setError(null);
    setColorNewTag(color);
  }

  /**
   * Deletion Step 1: Set the tagId to delete and open confirmation modal
   */
  function handleOnClickDeleteTag(tagId: number) {
    setDeleteTagId(tagId);
    setOpenConfirmDeletenBox(true);
  }

  /**
   * Deletion Step 2: Verify wether or not the user click in confirm deletion
   */
  function confirmTagDeletion(hasConfimed: any) {
    if (hasConfimed && deleteTagId) {
      deleteTag(deleteTagId);
    }
  }

  function handleTagCreation() {
    if (!showAddNewTag) {
      setShowAddNewTag(true);
      scrollCallDetailsContainer(120, 0);
    } else {
      createTag();
    }
  }

  /**
   * Reserve room on the right for the action icons so the clickable title
   * area doesn't overlap them. Uses calc() on the parent's width so the row
   * stays responsive to the container.
   */
  function getTagWidth(tag: Tag) {
    // eslint-disable-next-line
    const actionsContainerWidth = isAdministrator
      ? tag.assigned
        ? 100
        : 80
      : tag.assigned
      ? 40
      : 20;
    return `calc(100% - ${actionsContainerWidth}px)`;
  }

  /**
   * Render actions component such as Edit and Delete tags
   */
  function renderActions(nodeInfo: Tag) {
    const { id: tagId, tagName, isEditing, assigned } = nodeInfo;

    return (
      <ActionsContainer>
        {assigned === 1 && (
          <div className="assigned-tags">
            <Check className="close-icon" />
          </div>
        )}

        {isAdministrator && (
          <>
            {isEditing ? (
              <>
                <div role="presentation" onClick={() => clearEditionState()}>
                  <Close className="icon-edit" />
                </div>

                <CheckCircleOutline
                  role="presentation"
                  className="icon-edit"
                  onClick={() => confirmEdition(tagId)}
                />
              </>
            ) : (
              <>
                <Edit
                  role="presentation"
                  className="icon-edit"
                  onClick={() => initiateTagEdit(tagId, tagName)}
                />
                <Delete
                  className="icon-edit"
                  onClick={() => handleOnClickDeleteTag(tagId)}
                />
              </>
            )}
          </>
        )}
      </ActionsContainer>
    );
  }

  const tagsTreeFiltered = orderBasedOnBaseArray(
    allTagsDetails,
    filterTagsTreeByTagName(tagsTree, search)
  );

  if (open) {
    return (
      <Container className="addtags-container">
        <TagsCardHeader>
          <Close className="close-icon" onClick={handleClose} />
        </TagsCardHeader>

        <InputField
          margin="dense"
          variant="outlined"
          id="outlined-dense-multiline"
          placeholder={t('components.addTags.searchPlaceholder')}
          autoFocus
          onChange={(text: {
            target: { value: React.SetStateAction<string> };
          }) => setSearch(text.target.value)}
          value={search || ''}
        />

        <ListTags>
          <div style={{ height: 250 }}>
            {tagsTreeFiltered && (
              <SortableTree
                canDrag={false}
                // @ts-ignore
                canDrop={false}
                treeData={tagsTreeFiltered}
                rowHeight={55}
                generateNodeProps={({ node }: any) => ({
                  title: (
                    // eslint-disable-next-line
                    <div
                      className="rst__customTitleContainer"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        height: 30,
                        width: getTagWidth(node),
                      }}
                      onClick={() => expandAndAssignTag(node)}>
                      {node.isEditing ? (
                        <InputField
                          className="input-edit"
                          margin="dense"
                          variant="outlined"
                          autoFocus
                          onChange={(text: {
                            target: {
                              value: React.SetStateAction<string | null>;
                            };
                          }) => setUpdatedTagName(text.target.value)}
                          value={updatedTagName || node.tagName}
                        />
                      ) : (
                        <span>{node.tagName}</span>
                      )}
                    </div>
                  ),
                  buttons: [<>{renderActions(node)}</>],
                  className: 'rst__customUnsetDefaultNodeStyles',
                  style: {
                    ...getSortableTreeCustomStyles(node),
                  },
                })}
              />
            )}
          </div>
        </ListTags>

        {showAddNewTag && (
          <>
            <BoxNewTag>
              {TAG_DEFAULT_COLORS.map((color: string) => (
                <Item key={color} color={color} onClick={() => addColor(color)}>
                  {colorNewTag === color ? (
                    <Check className="icon-tags" />
                  ) : (
                    <Edit className="icon-tags" />
                  )}
                </Item>
              ))}
            </BoxNewTag>

            <InputTagName
              id="outlined-dense-multiline"
              margin="dense"
              variant="outlined"
              placeholder={t('components.addTags.inptChoosetag')}
              autoFocus
              onChange={(text: { target: { value: string } }) =>
                addTagName(text.target.value)
              }
              value={nameNewTag || ''}
              bgcolor={colorNewTag}
            />
            <BoxValidation error={error}>
              {error && <span className="error">{error}</span>}
              <span className="size">{`${nameNewTag.length} / ${TAG_NAME_MAX_LEGNTH}`}</span>
            </BoxValidation>
          </>
        )}

        <ButtonNewTag onClick={() => handleTagCreation()}>
          {!showAddNewTag ? (
            <span>{t('components.addTags.newTag')}</span>
          ) : (
            <span>
              {loading ? (
                <CircularProgress size={25} />
              ) : (
                t('components.addTags.save')
              )}
            </span>
          )}
        </ButtonNewTag>
        {showAddNewTag && (
          <span
            className="cancelBtn"
            onClick={() => cancel()}
            onKeyPress={() => cancel()}
            role="presentation">
            {t('components.addTags.cancel')}
          </span>
        )}
        <NuacomComponent.ConfirmationBox
          openModal={openConfirmDeletenBox}
          setOpenModal={setOpenConfirmDeletenBox}
          handleAction={confirmTagDeletion}
        />
      </Container>
    );
  }
  return null;
}

AddTags.defaultProps = {
  sipCallId: null,
  originalSipCallId: null,
};
